import { useState, useRef, useEffect } from "react";
import { Plus, Search } from "lucide-react";
import {useCompareStore} from "../../stores/useCompateStore";
import {useSuggestions} from "../../api/client";

export default function ComparisonSearchBar() {
    const [query, setQuery] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { isFull, addToCompare } = useCompareStore();

    const { data: suggestions = [], isFetching } = useSuggestions(query);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const handleSelect = (deviceId: number) => {
        if (isFull()) return;

        addToCompare(deviceId);
        setQuery("");
        setShowSuggestions(false);
    };

    return (
        <div
            ref={wrapperRef}
            className={`relative w-full max-w-md mb-8 transition-all duration-200 ${
                isFull() ? "opacity-50 cursor-not-allowed" : "opacity-100"
            }`}
        >
            <div className={`
                flex items-center bg-white rounded-xl shadow-sm border px-3 py-2 transition-all
                ${isFull() ? 'bg-gray-50 border-gray-200 pointer-events-none' : 'focus-within:border-blue-400 border-slate-200'}
            `}>
                <Search size={18} className={`${isFull() ? 'text-slate-300' : 'text-slate-400'} mr-2`} />
                <input
                    value={isFull() ? "" : query} // Clear text visually when disabled
                    onChange={(e) => !isFull() && setQuery(e.target.value)}
                    onFocus={() => !isFull() && setShowSuggestions(true)}
                    placeholder={isFull() ? "Comparison limit reached (4/4)" : "Add another phone..."}
                    disabled={isFull()}
                    className="flex-1 outline-none text-sm bg-transparent disabled:placeholder-slate-400"
                />

                {isFetching && !isFull() && (
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent ml-2" />
                )}
            </div>

            {/* Suggestions logic */}
            {!isFull() && showSuggestions && suggestions.length > 0 && (
                <ul className="absolute z-[100] w-full bg-white border rounded-b-xl shadow-lg mt-1 overflow-hidden">
                    {suggestions.map((item: { id: number; text: string; }) => (
                        <li
                            key={item.id}
                            onClick={() => handleSelect(item.id)}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm border-b last:border-none flex justify-between items-center group"
                        >
                            <span className="font-medium text-slate-700">{item.text}</span>
                            <div className="flex items-center gap-1 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Plus size={14} strokeWidth={3} />
                                <span className="text-xs font-bold">Add</span>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}